import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, OnInit, signal, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';

import { BarActionButton } from '../../../bar-action-button/bar-action-button';
import { BarActionButtonOptions } from '../../../bar-action-button/bar-action-button-options.model';
import { BarActionButtonType } from '../../../bar-action-button/bar-action-button-type.enum';
import { _BarInternalLayoutService } from '../../../layout/bar-internal-layout.service';
import { BarLayoutService } from '../bar-layout.service';
import { _BarLayoutAnchorDirective } from '../bar-layout-anchor.directive';
import { BarLayoutMenuComponent } from '../bar-layout-menu/bar-layout-menu.component';
import { BarMeerActiesComponent } from '../bar-meer-acties/bar-meer-acties.component';

@Component({
    selector: 'bar-layout-page',
    templateUrl: './bar-layout-page.component.html',
    host: { class: 'bar-layout-page-root' },
    imports: [
        MatSidenavModule,
        NgClass,
        BarLayoutMenuComponent,
        BarActionButton,
        _BarLayoutAnchorDirective,
        BarMeerActiesComponent,
        NgTemplateOutlet,
        RouterOutlet
    ]
})
export class BarLayoutPageComponent implements OnInit {
    readonly internalLayout = inject(_BarInternalLayoutService);
    readonly layout = inject(BarLayoutService);
    readonly #destroyRef = inject(DestroyRef);

    readonly mobileFriendly = input<boolean>(undefined);
    readonly touch = input<boolean>(undefined);
    readonly env = input<string>(undefined);
    readonly withRouting = input(true);
    readonly barLayoutPageAnchors = viewChildren(_BarLayoutAnchorDirective);
    readonly barLayoutMenuComponent = viewChild(BarLayoutMenuComponent);
    readonly internalContentActions = signal<BarActionButtonOptions[]>([]);
    readonly contentActions = signal<BarActionButtonOptions[]>(undefined);
    readonly moreActions = signal<BarActionButtonOptions[]>(undefined);
    readonly sidebar = signal<boolean>(undefined);
    readonly menuState = signal<string>(undefined);
    readonly mainAction = signal<BarActionButtonOptions>(undefined);
    readonly appVersion = signal<string>(undefined);
    readonly mainHeader = signal<string>(undefined);
    readonly offcanvasOpened = signal<boolean>(false);
    readonly offcanvasPinned = signal<boolean>(false);
    readonly submitActions = computed(() =>
        this.#submitActions()
            ? this.#submitActions().map(
                  (sa) =>
                      ({
                          // apply default settings first for submit actions
                          type: BarActionButtonType.Tonal,
                          color: 'dark',
                          ...sa
                      }) as BarActionButtonOptions
              )
            : this.#submitActions());

    readonly sidenavMode = computed<MatDrawerMode>(() => {
        /* Default info: window width < 1200px equals overlay mode, > 1200px equals pinned mode */
        const stateModeMapping: { [key: string]: MatDrawerMode } = {
            closed: 'side',
            overlay: 'over',
            pinned: 'side',
            default: this.smallDevice ? 'over' : 'side'
        };
        return this.menuState() ? stateModeMapping[this.menuState()] : stateModeMapping['default'];
    });
    /* based on the screen size we need to redefine the exact menu state (overlay or pinned) when in default mode */
    readonly menuStateExact = computed(() => {
        return this.menuState() === 'default' ? (this.smallDevice ? 'overlay' : 'pinned') : this.menuState();
    });

    readonly offcanvasMode = computed<MatDrawerMode>(() => (this.offcanvasPinned() ? 'side' : 'over'));

    readonly #submitActions = signal<BarActionButtonOptions[]>(undefined);

    get menuAction(): BarActionButtonOptions {
        return this.layout.menuAction;
    }

    get smallDevice(): boolean {
        return this.internalLayout.smallDevice;
    }

    ngOnInit(): void {
        this.internalLayout.internalContentActions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((contentActions) => {
            this.internalLayout.queueAction(() => {
                this.internalContentActions.set(contentActions || []);
            });
        });

        this.layout.submitActions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((submitActions) => {
            this.internalLayout.queueAction(() => {
                this.#submitActions.set(submitActions);
            });
        });

        this.layout.contentActions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((contentActions) => {
            this.internalLayout.queueAction(() => {
                this.contentActions.set(contentActions);
            });
        });

        this.layout.moreActions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((moreActions) => {
            this.internalLayout.queueAction(() => {
                this.moreActions.set(moreActions);
            });
        });

        this.layout.sidebar$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((sidebar) => {
            this.internalLayout.queueAction(() => {
                this.sidebar.set(sidebar);
            });
        });

        this.layout.menuState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((menuState) => {
            this.internalLayout.queueAction(() => {
                this.menuState.set(menuState);
            });
        });

        this.layout.mainAction$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((mainAction) => {
            this.internalLayout.queueAction(() => {
                this.mainAction.set(mainAction || this.internalLayout.internalMainAction);
            });
        });

        this.layout.appVersion$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((appVersion) => {
            this.internalLayout.queueAction(() => {
                this.appVersion.set(appVersion);
            });
        });

        this.layout.mainHeader$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((mainHeader) => {
            this.internalLayout.queueAction(() => {
                this.mainHeader.set(mainHeader);
            });
        });
    }

    handleBackdropEvent(event): void {
        this.layout.menuState$.next('closed');
    }
}
